import { VideoUpload } from '@sparemin/api-sdk';
import { fromJS } from 'immutable';
import { MediaImportedArgs } from 'containers/MediaUploadModal/types';
import {
  actions,
  NormalizedVideoUpload,
  UploadVideoOptions,
} from 'redux/middleware/api/media-upload-service';
import {
  abortMultipartDirectUpload,
  completeMultipartDirectUpload,
  initMultipartDirectUpload,
  presignMultipartDirectUploadParts,
  searchVideo,
  uploadVideo,
} from 'redux/middleware/api/media-upload-service/actions';
import { fetchTranscriptByVideoIdRequest } from 'redux/middleware/api/transcript-service/actions';
import { transcriptsSelector, videoSelector } from 'redux/modules/entities';
import { getStore } from 'redux/store/instance';
import { ThunkAction } from 'redux/types';
import { DeepImmutableMap } from 'types';
import {
  VIDEO_TRANSCRIPTION_POLL_INTERVAL_MILLIS,
  VIDEO_TRANSCRIPTION_POLL_MAX_ATTEMPTS,
} from 'utils/constants';
import {
  getMultipartPartNumbers,
  uploadPartsWithProgress,
} from 'utils/multipartUpload';
import reduxPoll from 'utils/redux-poll';
import { waitForVideoUpload } from '../common/actions/video';
import { Type } from './action-types';
import {
  isCurrentlyUploadingVideoSelector,
  isVideoUploadingSelector,
  multipartAbortOptionsSelector,
  uploadedVideoIdSelector,
  uploadIdentifierSelector,
} from './selectors';
import {
  BytesUploadResult,
  UploadSocialVideoOptions,
  UploadVideoToStorageOptions,
} from './types';
import {
  createSocialVideoUploadArgs,
  getFileIdentifier,
  getVideoSearchOptions,
} from './utils';

const TRANSCRIBE_POLL_ID = 'app/video-upload/transcribe-poll';

type SearchForVideoOptions = {
  /**
   * If true, will swallow errors and return undefined
   */
  suppressErrors?: boolean;
};

type UploadOptions = {
  /**
   * if true, will wait for the video upload to finish processing before returning,
   * otherwise will return with the partial video upload result in state
   */
  waitForUpload?: boolean;
};

export const searchForVideo = (
  opts: UploadVideoToStorageOptions,
  mediaImportedArgs?: MediaImportedArgs,
  searchOpts?: SearchForVideoOptions,
): ThunkAction<Promise<VideoUpload | undefined>> => async dispatch => {
  const { suppressErrors = false } = searchOpts || {};

  try {
    const videoSearchOptions = await getVideoSearchOptions(
      opts,
      mediaImportedArgs,
    );

    const { response } = await dispatch(searchVideo(videoSearchOptions));

    return response.data?.[0];
  } catch (error) {
    if (suppressErrors) {
      return undefined;
    }

    throw error;
  }
};

/**
 * Pushes bytes to S3 via the multipart upload protocol. Returns the
 * direct-upload bucket/key the register step needs. Progress is dispatched
 * to Redux as each part completes.
 */
const uploadVideoBytes = (
  file: File,
  identifier: string,
): ThunkAction<Promise<BytesUploadResult>> => async dispatch => {
  // 1. Init multipart upload.
  const {
    response: { uploadId, bucket, key, ...init },
  } = await dispatch(initMultipartDirectUpload(file, 'video'));

  dispatch({
    type: Type.MULTIPART_UPLOAD_INIT_SUCCESS,
    payload: {
      identifier,
      multipartAbortOptions: {
        uploadId,
        bucket,
        key,
      },
    },
  });

  // 2. Calculate part numbers.
  const partNumbers = getMultipartPartNumbers(
    file.size,
    init.partSizeBytes,
    init.maxParts,
  );

  // 3. Pre-sign all parts.
  const {
    response: { parts: presignedParts },
  } = await dispatch(
    presignMultipartDirectUploadParts({
      uploadId,
      bucket,
      key,
      partNumbers,
    }),
  );

  // 4. Upload all parts with concurrency and progress reporting.
  const completedParts = await uploadPartsWithProgress(
    file,
    presignedParts,
    init.partSizeBytes,
    progress =>
      dispatch({
        type: Type.VIDEO_UPLOAD_PROGRESS,
        payload: {
          identifier,
          progress,
        },
      }),
  );

  // 5. Complete the multipart upload.
  await dispatch(
    completeMultipartDirectUpload({
      uploadId,
      bucket,
      key,
      parts: completedParts,
    }),
  );

  return {
    bucket,
    key,
  };
};

/**
 * Registers the uploaded bytes with the backend. Args (trim, language,
 * initiateProcessing, waitForUpload) come from the caller. Returns both the
 * registered video entity and its id.
 */
const registerUploadedVideo = (
  opts: UploadVideoToStorageOptions,
  bytes: BytesUploadResult,
  uploadOpts?: UploadOptions,
): ThunkAction<Promise<{
  entity: DeepImmutableMap<NormalizedVideoUpload>;
  videoId: number;
}>> => async (dispatch, getState) => {
  const {
    trimStartMillis,
    trimEndMillis,
    language,
    initiateProcessing = true,
  } = opts;
  const { waitForUpload = false } = uploadOpts || {};

  const {
    response: { result: videoId },
  } = await dispatch(
    uploadVideo(undefined, {
      trimStart: trimStartMillis,
      trimEnd: trimEndMillis,
      transcribe: true,
      language,
      directUploadBucket: bytes.bucket,
      directUploadKey: bytes.key,
      initiateProcessing,
    }),
  );

  if (waitForUpload) {
    await dispatch(waitForVideoUpload(videoId));
  }

  return { entity: videoSelector(getState(), videoId), videoId };
};

/**
 * Resolves once the in-flight local-File upload for `identifier` settles.
 * Subscribes to the store rather than polling it: the single-occupancy
 * `videoUpload` slice flips `isUploading` false on completion, at which
 * point the registered video id is read back. Rejects if the upload fails
 * or is replaced by a different file.
 *
 * Subscribing (rather than dispatching from a thunk) needs the store
 * instance, which `redux/store/instance` exposes without the circular
 * import a direct `redux/store` dependency would create here.
 */
const subscribeToCurrentUpload = (identifier: string): Promise<number> =>
  new Promise<number>((resolve, reject) => {
    const store = getStore();

    const settle = (): boolean => {
      const state = store.getState();
      const isOurUpload = uploadIdentifierSelector(state) === identifier;

      // Our upload still owns the slice and is still running.
      if (isOurUpload && isVideoUploadingSelector(state)) {
        return false;
      }

      const videoId = uploadedVideoIdSelector(state);

      if (isOurUpload && videoId != null) {
        resolve(videoId);
      } else if (isOurUpload) {
        // Our upload still owns the slice but settled without a video id —
        // it genuinely failed or was cleared.
        reject(new Error('Video upload failed'));
      } else {
        // A different file claimed the single-occupancy slice before our
        // upload settled. Its outcome is no longer observable here — this is
        // not a failure of our upload, just a loss of visibility into it.
        reject(new Error('Video upload was superseded by another upload'));
      }

      return true;
    };

    // Guard against the upload settling between the caller's check and here.
    if (settle()) {
      return;
    }

    const unsubscribe = store.subscribe(() => {
      if (settle()) {
        unsubscribe();
      }
    });
  });

/**
 * Runs the local-File upload pipeline (cache lookup → multipart bytes →
 * register) as the sole initiator. Claims the single-occupancy `videoUpload`
 * slice for `identifier` via `VIDEO_UPLOAD_REQUEST` and records the
 * registered video id on the terminal action. Concurrent callers for the
 * same file observe this slice via `subscribeToCurrentUpload` instead of
 * running a second pipeline.
 */
const runLocalFileUpload = (
  file: File,
  identifier: string,
  opts: UploadVideoToStorageOptions,
  uploadOpts?: UploadOptions,
): ThunkAction<Promise<
  DeepImmutableMap<NormalizedVideoUpload>
>> => async dispatch => {
  dispatch({ type: Type.VIDEO_UPLOAD_REQUEST, payload: { identifier } });

  try {
    // Phase 1: cache lookup. A previously-uploaded file with the same md5
    // resolves immediately, with no bytes transfer.
    const cached = await dispatch(
      searchForVideo(opts, undefined, { suppressErrors: true }),
    );

    if (cached) {
      dispatch({
        type: Type.VIDEO_UPLOAD_SUCCESS,
        payload: { identifier, videoId: cached.id },
      });

      return fromJS(cached) as DeepImmutableMap<NormalizedVideoUpload>;
    }

    // Phase 2: push bytes to S3 via the multipart upload protocol.
    const bytes = await dispatch(uploadVideoBytes(file, identifier));

    // Phase 3: register the uploaded bytes with the backend.
    const { entity, videoId } = await dispatch(
      registerUploadedVideo(opts, bytes, uploadOpts),
    );

    dispatch({
      type: Type.VIDEO_UPLOAD_SUCCESS,
      payload: {
        identifier,
        videoUploadOptions: {
          directUploadBucket: bytes.bucket,
          directUploadKey: bytes.key,
        },
        videoId,
      },
    });

    return entity;
  } catch (error) {
    dispatch({ type: Type.VIDEO_UPLOAD_FAILURE, payload: { identifier } });
    throw error;
  }
};

/**
 * Cancels the upload occupying the single-occupancy `videoUpload` slice.
 *
 * Pass `identifier` to scope the cancel: the upload is only cancelled if it
 * still owns the slice. This stops a caller from aborting an upload it never
 * initiated — e.g. a hook unmounting with no video of its own, or one whose
 * upload has since been superseded by a different file. Called with no
 * identifier, it cancels whatever upload is current (user-initiated cancel).
 */
export const cancelVideoUpload = (identifier?: string): ThunkAction<void> => (
  dispatch,
  getState,
) => {
  const state = getState();

  if (identifier != null && uploadIdentifierSelector(state) !== identifier) {
    return;
  }

  const context = multipartAbortOptionsSelector(state);

  if (context) {
    dispatch(abortMultipartDirectUpload(context));
  }

  dispatch({ type: Type.VIDEO_UPLOAD_FAILURE, payload: { identifier } });
};

const uploadRemoteVideoFile = (
  src: string | undefined,
  args: UploadVideoOptions,
  opts?: UploadOptions,
): ThunkAction<Promise<DeepImmutableMap<NormalizedVideoUpload>>> => async (
  dispatch,
  getState,
) => {
  const { waitForUpload = false } = opts || {};

  dispatch({ type: Type.VIDEO_UPLOAD_REQUEST });

  try {
    const {
      response: { result: videoId },
    } = await dispatch(uploadVideo(src, args));

    if (waitForUpload) {
      await dispatch(waitForVideoUpload(videoId));
    }

    dispatch({
      type: Type.VIDEO_UPLOAD_SUCCESS,
      payload: { videoUploadOptions: args, videoId },
    });
    return videoSelector(getState(), videoId);
  } catch (error) {
    dispatch({ type: Type.VIDEO_UPLOAD_FAILURE });
    throw error;
  }
};

const uploadSocialVideo = (
  opts: UploadSocialVideoOptions,
  mediaImportedArgs: MediaImportedArgs,
  uploadOpts?: UploadOptions,
): ThunkAction<Promise<
  DeepImmutableMap<NormalizedVideoUpload>
>> => async dispatch =>
  dispatch(
    uploadRemoteVideoFile(
      undefined,
      createSocialVideoUploadArgs(opts, mediaImportedArgs),
      uploadOpts,
    ),
  );

/**
 * The single public entry point for uploading a video. Coordinates all
 * callers — wizard submit, transcribe, embed, anything else — through the
 * single-occupancy `videoUpload` slice.
 *
 * For a local File: idempotent against the slice. The first caller for a
 * given file identifier initiates the pipeline (search → multipart bytes →
 * register). Any concurrent caller for the same file observes the same
 * pipeline via `subscribeToCurrentUpload` and resolves with the same video.
 * The pipeline runs with the FIRST caller's args; later callers receive the
 * result but do not re-run register with different args. This is the
 * intentional contract — bytes are file-identity-determined and register is
 * fast, so we treat the first call as authoritative rather than running
 * register twice and creating duplicate backend records.
 *
 * For imported media or remote URLs: no dedup — these paths don't share an
 * initiator.
 */
export const uploadVideoToStorage = (
  opts: UploadVideoToStorageOptions,
  mediaImportedArgs?: MediaImportedArgs,
  uploadOpts?: UploadOptions,
): ThunkAction<Promise<DeepImmutableMap<VideoUpload>>> => async (
  dispatch,
  getState,
) => {
  const { src } = opts;

  // Local-File path — coordinated through the single-occupancy videoUpload
  // slice. Whichever caller arrives first initiates the pipeline; concurrent
  // callers for the same file join it via store subscription.
  if (src instanceof File && !mediaImportedArgs) {
    const identifier = getFileIdentifier(src);

    if (isCurrentlyUploadingVideoSelector(identifier)(getState())) {
      const videoId = await subscribeToCurrentUpload(identifier);

      return videoSelector(getState(), videoId);
    }

    return dispatch(runLocalFileUpload(src, identifier, opts, uploadOpts));
  }

  // Imported media + remote URL paths. These flows do not share an
  // initiator, so no dedup is needed.
  const existingVideo = await dispatch(
    searchForVideo(opts, mediaImportedArgs, { suppressErrors: true }),
  );

  if (existingVideo) {
    dispatch({
      type: Type.VIDEO_UPLOAD_SUCCESS,
      payload: { videoId: existingVideo.id },
    });
    return fromJS(existingVideo);
  }

  if (mediaImportedArgs) {
    return dispatch(uploadSocialVideo(opts, mediaImportedArgs, uploadOpts));
  }

  // typeof src === 'string' — legacy remote-URL path.
  return dispatch(
    uploadRemoteVideoFile(
      src as string,
      {
        trimStart: opts.trimStartMillis,
        trimEnd: opts.trimEndMillis,
        transcribe: true,
        language: opts.language,
        initiateProcessing: true,
      },
      uploadOpts,
    ),
  );
};

export const awaitVideoTranscription = (
  id: number,
): ThunkAction<Promise<any>> => (dispatch, getState) =>
  reduxPoll(dispatch, () => dispatch(actions.getVideo(id)), {
    id: TRANSCRIBE_POLL_ID,
    intervalMillis: VIDEO_TRANSCRIPTION_POLL_INTERVAL_MILLIS,
    maxAttempts: VIDEO_TRANSCRIPTION_POLL_MAX_ATTEMPTS,
    shouldContinue: err => {
      if (err) return false;
      const isResolved = transcriptsSelector(getState()).getIn([
        String(id),
        'isResolved',
      ]);
      return !isResolved;
    },
  }).then(() => dispatch(fetchTranscriptByVideoIdRequest(id)));
